import { useState, useEffect, useRef } from "react";
import { T, Icon, fmtKES, StatusPill } from "../businessTheme";
import { MOCK_INVOICES, PLAN_LIMITS, PLAN_ORDER } from "../businessMockData";
import { supabase } from "../../lib/supabase";


/* ── Invoice row — accepts onDownload callback ── */
function InvoiceRow({ inv, onDownload }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr 1fr 0.8fr", padding: "16px 20px", alignItems: "center", borderBottom: `1px solid ${T.n100}` }}>
      <div>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: T.n900 }}>{inv.period}</div>
        <div style={{ fontSize: 11.5, color: T.n400 }}>{inv.lineItems ?? inv.line_items?.length ?? 0} line item{(inv.lineItems ?? inv.line_items?.length ?? 0) !== 1 ? "s" : ""}</div>
      </div>
      <div style={{ fontSize: 13, color: T.n600 }}>
        {inv.status === "paid"
          ? `Paid ${inv.paidOn ?? inv.paid_on}`
          : `Due ${inv.dueDate ?? inv.due_date}`}
      </div>
      <div><StatusPill status={inv.status} /></div>
      <div style={{ fontSize: 14, fontWeight: 700, color: T.n900 }}>{fmtKES(inv.total)}</div>
      <button
        onClick={onDownload}
        style={{ background: "none", border: "none", cursor: "pointer", color: T.g600, display: "flex", alignItems: "center", gap: 5, fontSize: 12.5, fontWeight: 600, fontFamily: T.sans }}>
        <Icon name="Download" size={14} strokeWidth={2} /> PDF
      </button>
    </div>
  );
}

/* ── Payment method switcher ── */
function PaymentMethodCard({ account, onChangeMethod }) {
  const [editing, setEditing] = useState(false);
  const [method, setMethod] = useState(account.paymentMethod);
  const [bank, setBank] = useState(account.bankDetails);

  const inputStyle = {
    width: "100%", padding: "10px 13px", borderRadius: 9,
    border: `1.5px solid ${T.n200}`, fontSize: 13, fontFamily: T.sans,
    outline: "none", boxSizing: "border-box", color: T.n900,
  };

  const handleSave = () => {
    onChangeMethod(method, bank);
    setEditing(false);
  };

  return (
    <div style={{ padding: "20px 22px", borderRadius: 14, border: `1px solid ${T.n150}`, background: T.white }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: editing ? 18 : 0, flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: T.n50, border: `1px solid ${T.n150}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon name={method === "mpesa" ? "Phone" : "Landmark"} size={18} color={T.n500} strokeWidth={1.7} />
          </div>
          <div>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: T.n900 }}>
              {method === "mpesa"
                ? `M-Pesa · ${account.phone}`
                : (bank.bankName
                  ? `${bank.bankName} · ${bank.accountNumber ? "\u2022\u2022\u2022" + bank.accountNumber.slice(-4) : "Not set"}`
                  : "Bank transfer · Not configured")}
            </div>
            <div style={{ fontSize: 12, color: T.n400 }}>
              {method === "mpesa" ? "Billed monthly via STK push" : "Billed monthly via bank transfer"}
            </div>
          </div>
        </div>
        <button onClick={() => setEditing(o => !o)} style={{ padding: "9px 16px", borderRadius: 9, border: `1.5px solid ${T.n200}`, background: "transparent", color: T.n700, fontSize: 12.5, fontWeight: 600, fontFamily: T.sans, cursor: "pointer" }}>
          {editing ? "Close" : "Change payment method"}
        </button>
      </div>

      {editing && (
        <div style={{ marginTop: 18, paddingTop: 18, borderTop: `1px solid ${T.n100}` }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
            <button onClick={() => setMethod("mpesa")} style={{
              flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              padding: "12px", borderRadius: 11, border: `1.5px solid ${method === "mpesa" ? T.g500 : T.n150}`,
              background: method === "mpesa" ? T.g50 : T.white, cursor: "pointer", fontFamily: T.sans,
            }}>
              <Icon name="Phone" size={15} color={method === "mpesa" ? T.g600 : T.n400} strokeWidth={1.8} />
              <span style={{ fontSize: 13, fontWeight: 600, color: method === "mpesa" ? T.g700 : T.n600 }}>M-Pesa</span>
            </button>
            <button onClick={() => setMethod("bank")} style={{
              flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              padding: "12px", borderRadius: 11, border: `1.5px solid ${method === "bank" ? T.g500 : T.n150}`,
              background: method === "bank" ? T.g50 : T.white, cursor: "pointer", fontFamily: T.sans,
            }}>
              <Icon name="Landmark" size={15} color={method === "bank" ? T.g600 : T.n400} strokeWidth={1.8} />
              <span style={{ fontSize: 13, fontWeight: 600, color: method === "bank" ? T.g700 : T.n600 }}>Bank account</span>
            </button>
          </div>

          {method === "mpesa" && (
            <div style={{ marginBottom: 4 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: T.n700, display: "block", marginBottom: 6 }}>M-Pesa phone number</label>
              <input defaultValue={account.phone} style={inputStyle} />
            </div>
          )}

          {method === "bank" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: T.n700, display: "block", marginBottom: 6 }}>Bank name</label>
                <input placeholder="e.g. KCB, Equity, Co-operative Bank" defaultValue={bank.bankName}
                  onChange={e => setBank(b => ({ ...b, bankName: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: T.n700, display: "block", marginBottom: 6 }}>Branch</label>
                <input placeholder="e.g. Westlands Branch" defaultValue={bank.branch}
                  onChange={e => setBank(b => ({ ...b, branch: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: T.n700, display: "block", marginBottom: 6 }}>Account number</label>
                <input placeholder="Account number" defaultValue={bank.accountNumber}
                  onChange={e => setBank(b => ({ ...b, accountNumber: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: T.n700, display: "block", marginBottom: 6 }}>Account name</label>
                <input placeholder="As it appears on the account" defaultValue={bank.accountName}
                  onChange={e => setBank(b => ({ ...b, accountName: e.target.value }))} style={inputStyle} />
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: T.n700, display: "block", marginBottom: 6 }}>SWIFT / Bank code</label>
                <input placeholder="e.g. KCBLKENX" defaultValue={bank.swiftCode}
                  onChange={e => setBank(b => ({ ...b, swiftCode: e.target.value }))} style={inputStyle} />
              </div>
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
            <button onClick={() => setEditing(false)} style={{ padding: "10px 18px", borderRadius: 10, border: `1.5px solid ${T.n200}`, background: "transparent", color: T.n700, fontSize: 13, fontWeight: 600, fontFamily: T.sans, cursor: "pointer" }}>
              Cancel
            </button>
            <button onClick={handleSave} style={{ padding: "10px 22px", borderRadius: 10, border: "none", background: T.g500, color: T.white, fontSize: 13, fontWeight: 600, fontFamily: T.sans, cursor: "pointer" }}
              onMouseEnter={e => e.currentTarget.style.background = T.g600}
              onMouseLeave={e => e.currentTarget.style.background = T.g500}>
              Save payment method
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Plan comparison table ── */
function PlanComparisonTable({ currentPlanKey, onSelectPlan }) {
  const rows = [
    { key: "maxPros",          label: "Reserved Pros" },
    { key: "bookingsPerMonth", label: "Bookings / month" },
    { key: "maxTeamMembers",   label: "Team members" },
    { key: "proPool",          label: "Pro pool access",   format: v => v === "priority" ? "Business Priority" : "Standard" },
    { key: "prioritySupport",  label: "Priority support",  format: v => v ? "Yes" : "No" },
    { key: "apiAccess",        label: "API access",        format: v => v ? "Yes" : "No" },
  ];

  return (
    <div style={{ overflowX: "auto" }}>
      <div style={{ display: "grid", gridTemplateColumns: `1.2fr repeat(${PLAN_ORDER.length}, 1fr)`, minWidth: 640 }}>
        {/* Header row */}
        <div />
        {PLAN_ORDER.map(key => {
          const p = PLAN_LIMITS[key];
          const isCurrent = key === currentPlanKey;
          return (
            <div key={key} style={{
              padding: "14px 12px", textAlign: "center", borderRadius: "12px 12px 0 0",
              background: isCurrent ? T.g50 : T.n50, border: isCurrent ? `1.5px solid ${T.g200}` : `1px solid ${T.n150}`,
              borderBottom: "none",
            }}>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: isCurrent ? T.g700 : T.n900 }}>{p.label}</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: T.n900, marginTop: 4 }}>
                {p.price ? fmtKES(p.price) : "Custom"}
              </div>
              {p.price && <div style={{ fontSize: 10.5, color: T.n400 }}>/month</div>}
            </div>
          );
        })}

        {/* Data rows */}
        {rows.map((row, ri) => (
          <>
            <div key={`${row.key}-label`} style={{
              padding: "11px 14px", fontSize: 12.5, fontWeight: 600, color: T.n600,
              borderTop: `1px solid ${T.n100}`, display: "flex", alignItems: "center",
            }}>
              {row.label}
            </div>
            {PLAN_ORDER.map(key => {
              const p = PLAN_LIMITS[key];
              const isCurrent = key === currentPlanKey;
              const raw = p[row.key];
              const display = row.format ? row.format(raw) : (raw === null || raw === undefined ? "Negotiated" : raw);
              return (
                <div key={`${row.key}-${key}`} style={{
                  padding: "11px 12px", textAlign: "center", fontSize: 13, fontWeight: 600, color: T.n800,
                  background: isCurrent ? T.g50 : T.white, borderTop: `1px solid ${T.n100}`,
                  borderLeft: `1px solid ${T.n150}`,
                }}>
                  {display}
                </div>
              );
            })}
          </>
        ))}

        {/* Footer CTA row */}
        <div />
        {PLAN_ORDER.map(key => {
          const isCurrent = key === currentPlanKey;
          return (
            <div key={`${key}-cta`} style={{
              padding: "14px 12px", textAlign: "center", borderRadius: "0 0 12px 12px",
              background: isCurrent ? T.g50 : T.white, border: isCurrent ? `1.5px solid ${T.g200}` : `1px solid ${T.n150}`,
              borderTop: "none",
            }}>
              {isCurrent ? (
                <span style={{ fontSize: 12, fontWeight: 700, color: T.g700, display: "inline-flex", alignItems: "center", gap: 5 }}>
                  <Icon name="CheckCircle" size={13} color={T.g600} strokeWidth={2} /> Current plan
                </span>
              ) : key === "custom" ? (
                <button onClick={() => onSelectPlan(key)} style={{ padding: "8px 14px", borderRadius: 8, border: `1.5px solid ${T.n200}`, background: "transparent", color: T.n700, fontSize: 12, fontWeight: 600, fontFamily: T.sans, cursor: "pointer" }}>
                  Contact sales
                </button>
              ) : (
                <button onClick={() => onSelectPlan(key)} style={{ padding: "8px 14px", borderRadius: 8, border: "none", background: T.g500, color: T.white, fontSize: 12, fontWeight: 600, fontFamily: T.sans, cursor: "pointer" }}
                  onMouseEnter={e => e.currentTarget.style.background = T.g600}
                  onMouseLeave={e => e.currentTarget.style.background = T.g500}>
                  {PLAN_ORDER.indexOf(key) > PLAN_ORDER.indexOf(currentPlanKey) ? "Upgrade" : "Switch"}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Plan change confirmation + real payment flow ── */
function ChangePlanModal({ open, onClose, targetPlanKey, currentAccount, onConfirm }) {
  const [step, setStep] = useState("confirm");
  const [payMethod, setPayMethod] = useState(currentAccount?.paymentMethod ?? "mpesa");
  const [mpesaPhone, setMpesaPhone] = useState(currentAccount?.phone ?? "");
  const [error, setError] = useState("");
  const pollRef = useRef(null);
  const paymentIdRef = useRef(null);

  useEffect(() => {
    if (open) { setStep("confirm"); setError(""); }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [open]);

  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  useEffect(() => {
    if (step !== "processing" || !targetPlanKey) return;

    const plan = PLAN_LIMITS[targetPlanKey];

    const run = async () => {
      try {
        if (payMethod === "mpesa") {
          const { data, error: fnErr } = await supabase.functions.invoke("mpesa-initiate-plan", {
            body: {
              phone: mpesaPhone.replace(/\s/g, ""),
              amount: plan.price,
              accountReference: `JiraniApp_Plan_${targetPlanKey}`,
              businessId: currentAccount.id,
              planKey: targetPlanKey,
            },
          });

          if (fnErr || data?.error) {
            setError(data?.error ?? "M-Pesa STK push failed. Try again.");
            setStep("failed");
            return;
          }

          paymentIdRef.current = data?.paymentId ?? null;

          if (!paymentIdRef.current) {
            setError("Payment was initiated but could not be tracked. Please check your invoices in a few minutes.");
            setStep("failed");
            return;
          }

          setStep("polling");
        } else {
          // Bank transfer is intentionally out of scope for launch — no
          // send-bank-transfer-instructions function exists yet. Fail
          // honestly rather than showing a false success screen.
          setError("Bank transfer is not available yet. Please pay via M-Pesa for now.");
          setStep("failed");
          return;
        }
      } catch (err) {
        console.error("Plan payment failed:", err);
        setError("Unexpected error. Please try again.");
        setStep("failed");
      }
    };

    run();
  }, [step, targetPlanKey, payMethod, mpesaPhone, currentAccount]);

  useEffect(() => {
    if (step !== "polling" || !paymentIdRef.current) return;

    let attempts = 0;
    const MAX_ATTEMPTS = 20;
    const INTERVAL_MS = 3000;

    pollRef.current = setInterval(async () => {
      attempts += 1;

      const { data, error: pollErr } = await supabase
        .from("plan_payments")
        .select("status")
        .eq("id", paymentIdRef.current)
        .single();

      if (pollErr) {
        clearInterval(pollRef.current);
        setError("Could not confirm payment status. Check your invoices shortly.");
        setStep("failed");
        return;
      }

      if (data?.status === "confirmed") {
        clearInterval(pollRef.current);
        setStep("success");
        return;
      }

      if (data?.status === "failed") {
        clearInterval(pollRef.current);
        setError("M-Pesa payment was not completed.");
        setStep("failed");
        return;
      }

      if (attempts >= MAX_ATTEMPTS) {
        clearInterval(pollRef.current);
        setError("We haven't received confirmation yet. If you entered your M-Pesa PIN, this can take a few minutes — check your invoices shortly.");
        setStep("failed");
      }
    }, INTERVAL_MS);

    return () => clearInterval(pollRef.current);
  }, [step]);

  if (!open || !targetPlanKey) return null;
  const plan = PLAN_LIMITS[targetPlanKey];

  if (step === "confirm") {
    return (
      <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 500, background: "rgba(15,17,23,.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
        <div onClick={e => e.stopPropagation()} style={{ width: "min(440px, 100%)", background: T.white, borderRadius: 18, padding: 26 }}>
          <h3 style={{ fontFamily: T.serif, fontSize: 22, fontWeight: 400, color: T.n900, margin: "0 0 10px" }}>
            Switch to {plan.label}?
          </h3>
          <p style={{ fontSize: 13, color: T.n500, margin: "0 0 18px", lineHeight: 1.6 }}>
            Your subscription will change to <strong>{fmtKES(plan.price)}/month</strong>.
            You will have {plan.maxPros} reserved Pro slots, {plan.bookingsPerMonth} bookings/month,
            and {plan.maxTeamMembers} team seats.
          </p>

          <div style={{ marginBottom: 18 }}>
            <p style={{ fontSize: 12.5, fontWeight: 600, color: T.n700, margin: "0 0 10px" }}>Payment method</p>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={() => setPayMethod("mpesa")}
                style={{
                  flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  padding: "12px", borderRadius: 11,
                  border: `1.5px solid ${payMethod === "mpesa" ? T.g500 : T.n150}`,
                  background: payMethod === "mpesa" ? T.g50 : T.white,
                  cursor: "pointer", fontFamily: T.sans,
                }}>
                <Icon name="Phone" size={15} color={payMethod === "mpesa" ? T.g600 : T.n400} strokeWidth={1.8} />
                <span style={{ fontSize: 13, fontWeight: 600, color: payMethod === "mpesa" ? T.g700 : T.n600 }}>M-Pesa</span>
              </button>
              <button
                disabled
                title="Bank transfer is coming soon"
                style={{
                  flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  padding: "12px", borderRadius: 11,
                  border: `1.5px solid ${T.n150}`,
                  background: T.n50, opacity: 0.5, cursor: "not-allowed", fontFamily: T.sans,
                }}>
                <Icon name="Landmark" size={15} color={T.n400} strokeWidth={1.8} />
                <span style={{ fontSize: 13, fontWeight: 600, color: T.n500 }}>Bank transfer (coming soon)</span>
              </button>
            </div>
          </div>

          {payMethod === "mpesa" && (
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontSize: 12.5, fontWeight: 600, color: T.n700, display: "block", marginBottom: 6 }}>
                M-Pesa phone number
              </label>
              <input
                value={mpesaPhone}
                onChange={e => setMpesaPhone(e.target.value)}
                placeholder="e.g. 0712 345 678"
                style={{ width: "100%", padding: "11px 14px", borderRadius: 10, border: `1.5px solid ${T.n200}`, fontSize: 13.5, fontFamily: T.sans, outline: "none", boxSizing: "border-box" }}
              />
            </div>
          )}

          {payMethod === "bank" && (
            <div style={{ marginBottom: 18, padding: "14px", borderRadius: 10, background: T.blueBg, border: `1px solid ${T.blueBorder}` }}>
              <p style={{ fontSize: 12.5, color: T.blue, margin: 0, lineHeight: 1.7 }}>
                After confirming, you will receive bank transfer instructions via email.
                Your plan will be activated once payment is verified (1-2 business days).
              </p>
            </div>
          )}

          {error && (
            <p style={{ fontSize: 12.5, color: T.red, margin: "0 0 12px" }}>{error}</p>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button onClick={onClose} style={{ padding: "10px 18px", borderRadius: 10, border: `1.5px solid ${T.n200}`, background: "transparent", color: T.n700, fontSize: 13, fontWeight: 600, fontFamily: T.sans, cursor: "pointer" }}>
              Cancel
            </button>
            <button
              onClick={() => {
                if (payMethod === "mpesa" && mpesaPhone.replace(/\s/g, "").length < 9) {
                  setError("Enter a valid M-Pesa phone number.");
                  return;
                }
                setError("");
                setStep("processing");
              }}
              style={{ padding: "10px 22px", borderRadius: 10, border: "none", background: T.g500, color: T.white, fontSize: 13, fontWeight: 600, fontFamily: T.sans, cursor: "pointer" }}>
              Confirm &amp; Pay {fmtKES(plan.price)}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === "processing") {
    return (
      <div style={{ position: "fixed", inset: 0, zIndex: 500, background: "rgba(15,17,23,.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
        <div style={{ width: "min(360px, 100%)", background: T.white, borderRadius: 18, padding: 32, textAlign: "center" }}>
          <div style={{ width: 56, height: 56, borderRadius: "50%", background: T.g50, border: `2px solid ${T.g200}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 18px" }}>
            <Icon name={payMethod === "mpesa" ? "Phone" : "Landmark"} size={24} color={T.g500} strokeWidth={1.6} />
          </div>
          <h3 style={{ fontFamily: T.serif, fontSize: 20, fontWeight: 400, color: T.n900, margin: "0 0 10px" }}>
            {payMethod === "mpesa" ? "Sending M-Pesa prompt..." : "Processing..."}
          </h3>
          <p style={{ fontSize: 13, color: T.n500, lineHeight: 1.6, margin: 0 }}>
            {payMethod === "mpesa"
              ? `Sending a payment prompt to ${mpesaPhone}.`
              : "Setting up your bank transfer. This will only take a moment."}
          </p>
        </div>
      </div>
    );
  }

  if (step === "polling") {
    return (
      <div style={{ position: "fixed", inset: 0, zIndex: 500, background: "rgba(15,17,23,.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
        <div style={{ width: "min(360px, 100%)", background: T.white, borderRadius: 18, padding: 32, textAlign: "center" }}>
          <div style={{
            width: 56, height: 56, borderRadius: "50%", background: T.g50, border: `2px solid ${T.g200}`,
            display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 18px",
          }}>
            <Icon name="Phone" size={24} color={T.g500} strokeWidth={1.6} />
          </div>
          <h3 style={{ fontFamily: T.serif, fontSize: 20, fontWeight: 400, color: T.n900, margin: "0 0 10px" }}>
            Check your phone
          </h3>
          <p style={{ fontSize: 13, color: T.n500, lineHeight: 1.6, margin: 0 }}>
            An M-Pesa prompt has been sent to {mpesaPhone}. Enter your PIN to complete the
            payment — this screen will update automatically.
          </p>
        </div>
      </div>
    );
  }

  if (step === "success") {
    return (
      <div style={{ position: "fixed", inset: 0, zIndex: 500, background: "rgba(15,17,23,.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
        <div style={{ width: "min(380px, 100%)", background: T.white, borderRadius: 18, padding: 32, textAlign: "center" }}>
          <div style={{ width: 56, height: 56, borderRadius: "50%", background: T.g50, border: `2px solid ${T.g200}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 18px" }}>
            <Icon name="CheckCircle" size={26} color={T.g500} strokeWidth={1.8} />
          </div>
          <h3 style={{ fontFamily: T.serif, fontSize: 22, fontWeight: 400, color: T.n900, margin: "0 0 10px" }}>
            {payMethod === "bank" ? "Request received!" : "Payment confirmed!"}
          </h3>
          <p style={{ fontSize: 13, color: T.n500, lineHeight: 1.6, margin: "0 0 24px" }}>
            {payMethod === "bank"
              ? `Bank transfer instructions have been sent to ${currentAccount.email}. Your ${plan.label} plan will activate once payment clears.`
              : `Your ${plan.label} plan is now active. Enjoy your upgraded features.`}
          </p>
          <button
            onClick={() => {
              if (payMethod === "mpesa") {
                onConfirm(targetPlanKey);
              }
              onClose();
            }}
            style={{ padding: "11px 28px", borderRadius: 10, border: "none", background: T.g500, color: T.white, fontSize: 13, fontWeight: 600, fontFamily: T.sans, cursor: "pointer" }}>
            Done
          </button>
        </div>
      </div>
    );
  }

  if (step === "failed") {
    return (
      <div style={{ position: "fixed", inset: 0, zIndex: 500, background: "rgba(15,17,23,.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
        <div style={{ width: "min(380px, 100%)", background: T.white, borderRadius: 18, padding: 32, textAlign: "center" }}>
          <div style={{ width: 56, height: 56, borderRadius: "50%", background: T.redBg, border: `2px solid ${T.redBorder}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 18px" }}>
            <Icon name="AlertTriangle" size={24} color={T.red} strokeWidth={1.8} />
          </div>
          <h3 style={{ fontFamily: T.serif, fontSize: 20, fontWeight: 400, color: T.n900, margin: "0 0 10px" }}>Payment failed</h3>
          <p style={{ fontSize: 13, color: T.n500, lineHeight: 1.6, margin: "0 0 20px" }}>{error || "Something went wrong. Please try again."}</p>
          <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
            <button onClick={onClose} style={{ padding: "10px 18px", borderRadius: 10, border: `1.5px solid ${T.n200}`, background: "transparent", color: T.n700, fontSize: 13, fontWeight: 600, fontFamily: T.sans, cursor: "pointer" }}>
              Cancel
            </button>
            <button onClick={() => { setError(""); setStep("confirm"); }} style={{ padding: "10px 22px", borderRadius: 10, border: "none", background: T.g500, color: T.white, fontSize: 13, fontWeight: 600, fontFamily: T.sans, cursor: "pointer" }}>
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

/* ════════════════════════════════════════════════════════════ */

function pdfExportStub(setToast) {
  console.warn("PDF export not available — jspdf not installed");
  setToast("PDF export is temporarily unavailable.");
  setTimeout(() => setToast(null), 4000);
}

export default function BillingSection({ account, plan, userRole, onUpdateAccount, onNavigate }) {
  const [compareOpen, setCompareOpen] = useState(false);
  const [targetPlan, setTargetPlan] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [pdfToast, setPdfToast] = useState(null);

  // FIX: useEffect must be called unconditionally — guard moved inside the effect
  useEffect(() => {
    if (userRole !== "Owner") return;

    const loadInvoices = async () => {
      setInvoicesLoading(true);
      const { data, error } = await supabase
        .from("invoices")
        .select("*")
        .eq("business_id", account.id)
        .order("created_at", { ascending: false });

      if (!error && data && data.length > 0) {
        setInvoices(data);
      } else {
        setInvoices(MOCK_INVOICES);
      }
      setInvoicesLoading(false);
    };
    loadInvoices();
  }, [account.id, userRole]);

  // Owner-only gate — rendered after all hooks
  if (userRole !== "Owner") {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "80px 32px", gap: 16, textAlign: "center" }}>
        <div style={{ width: 60, height: 60, borderRadius: 16, background: T.n50, border: `1px solid ${T.n150}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon name="Lock" size={26} color={T.n400} strokeWidth={1.5} />
        </div>
        <div>
          <h3 style={{ fontFamily: T.serif, fontSize: 22, fontWeight: 400, color: T.n900, margin: "0 0 8px" }}>
            Owner access required
          </h3>
          <p style={{ fontSize: 13.5, color: T.n500, margin: 0, lineHeight: 1.7, maxWidth: 360 }}>
            Only the account owner can manage billing.
          </p>
        </div>
      </div>
    );
  }

  const bookingsUsed  = account.bookingsUsedThisMonth ?? 0;
  const bookingsPct   = plan.bookingsPerMonth ? Math.min(100, Math.round((bookingsUsed / plan.bookingsPerMonth) * 100)) : 0;
  const atBookingLimit = plan.bookingsPerMonth && bookingsUsed >= plan.bookingsPerMonth;

  const upcoming = invoices.find(i => i.status === "upcoming");
  const isPastDue = account.subscription_status === "past_due";

  const handleChangeMethod = async (method, bankDetails) => {
    onUpdateAccount?.({ ...account, paymentMethod: method, bankDetails });

    const { error } = await supabase
      .from("business_accounts")
      .update({
        payment_method:       method,
        bank_name:            bankDetails?.bankName      ?? null,
        bank_branch:          bankDetails?.branch        ?? null,
        bank_account_number:  bankDetails?.accountNumber ?? null,
        bank_account_name:    bankDetails?.accountName   ?? null,
        bank_swift_code:      bankDetails?.swiftCode     ?? null,
      })
      .eq("id", account.id);

    if (error) {
      console.error("Failed to update payment method:", error);
    }
  };

  const handleSelectPlan = (planKey) => {
    if (planKey === "custom") return;
    setTargetPlan(planKey);
  };

  const handleConfirmPlanChange = async (planKey) => {
    onUpdateAccount?.({ ...account, plan: planKey });
    setTargetPlan(null);
    setCompareOpen(false);

    const { error } = await supabase
      .from("business_accounts")
      .update({
        plan: planKey,
        plan_updated_at: new Date().toISOString(),
      })
      .eq("id", account.id);

    if (error) {
      console.error("Failed to update plan in database:", error);
    }
  };

  const handleDownloadInvoice = (_inv) => {
    pdfExportStub(setPdfToast);
  };

  return (
    <div>
      {isPastDue && (
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12,
          padding: "16px 20px", borderRadius: 14, marginBottom: 20,
          background: T.redBg, border: `1.5px solid ${T.redBorder}`,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Icon name="AlertTriangle" size={20} color={T.red} strokeWidth={2} />
            <div>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: T.red, marginBottom: 2 }}>
                Your payment is overdue
              </div>
              <div style={{ fontSize: 12.5, color: T.red, opacity: 0.85 }}>
                Access to some features has been restricted. Please pay now to restore full access.
              </div>
            </div>
          </div>
          <button
            onClick={() => setTargetPlan(plan.key)}
            style={{
              padding: "10px 20px", borderRadius: 10, border: "none",
              background: T.red, color: T.white,
              fontSize: 13, fontWeight: 700, fontFamily: T.sans, cursor: "pointer", whiteSpace: "nowrap",
            }}>
            Pay now
          </button>
        </div>
      )}

      {pdfToast && (
        <div style={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", zIndex: 600,
          padding: "12px 22px", borderRadius: 12, background: T.n900, color: T.white,
          fontSize: 13, fontWeight: 600, boxShadow: "0 4px 20px rgba(0,0,0,.18)",
          display: "flex", alignItems: "center", gap: 10,
        }}>
          <Icon name="AlertCircle" size={15} color={T.white} strokeWidth={2} />
          {pdfToast}
        </div>
      )}

      {/* Plan + next invoice summary */}
      <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 16, marginBottom: 20 }}>
        <div style={{ padding: "22px", borderRadius: 16, background: T.g500, color: T.white }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", opacity: 0.85 }}>Current plan</span>
            <Icon name="Crown" size={16} color="#fff" strokeWidth={1.7} />
          </div>
          <div style={{ fontFamily: T.serif, fontSize: 28, fontWeight: 400, marginBottom: 4 }}>{plan.label}</div>
          <div style={{ fontSize: 13, opacity: 0.85, marginBottom: 14 }}>
            {plan.price ? `${fmtKES(plan.price)} / month, all-inclusive` : "Custom pricing"}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 18 }}>
            {[
              ["Reserved Pros",     plan.maxPros          ?? "Negotiated"],
              ["Bookings / month",  plan.bookingsPerMonth ?? "Negotiated"],
              ["Team seats",        plan.maxTeamMembers   ?? "Negotiated"],
            ].map(([label, val]) => (
              <div key={label} style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5 }}>
                <span style={{ opacity: 0.8 }}>{label}</span>
                <span style={{ fontWeight: 700 }}>{val}</span>
              </div>
            ))}
          </div>
          <button onClick={() => setCompareOpen(true)} style={{ padding: "9px 16px", borderRadius: 9, border: "1.5px solid rgba(255,255,255,.4)", background: "rgba(255,255,255,.12)", color: "#fff", fontSize: 12.5, fontWeight: 600, fontFamily: T.sans, cursor: "pointer" }}>
            Compare plans
          </button>
        </div>

        <div style={{ padding: "22px", borderRadius: 16, border: `1px solid ${T.n150}`, background: T.white }}>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: T.n400, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 14 }}>Next invoice</div>
          {upcoming ? (
            <>
              <div style={{ fontFamily: T.serif, fontSize: 28, fontWeight: 400, color: T.n900, marginBottom: 4 }}>{fmtKES(upcoming.total)}</div>
              <div style={{ fontSize: 12.5, color: T.n400, marginBottom: 16 }}>Due {upcoming.dueDate ?? upcoming.due_date}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
                {[
                  ["Subscription",        upcoming.subscriptionFee  ?? upcoming.subscription_fee  ?? 0],
                  ["On-demand bookings",  upcoming.onDemandCharges  ?? upcoming.on_demand_charges ?? 0],
                ].map(([label, val]) => (
                  <div key={label} style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5 }}>
                    <span style={{ color: T.n500 }}>{label}</span>
                    <span style={{ color: T.n800, fontWeight: 600 }}>{fmtKES(val)}</span>
                  </div>
                ))}
              </div>
              <div style={{ paddingTop: 14, borderTop: `1px solid ${T.n100}` }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 7 }}>
                  <span style={{ fontSize: 11.5, fontWeight: 600, color: T.n500 }}>Bookings used this month</span>
                  <span style={{ fontSize: 11.5, fontWeight: 700, color: atBookingLimit ? T.red : T.n800 }}>
                    {bookingsUsed} / {plan.bookingsPerMonth ?? "\u221e"}
                  </span>
                </div>
                {plan.bookingsPerMonth && (
                  <div style={{ height: 6, borderRadius: 99, background: T.n100, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${bookingsPct}%`, background: atBookingLimit ? T.red : T.g500, borderRadius: 99 }} />
                  </div>
                )}
                {atBookingLimit && (
                  <p style={{ fontSize: 11.5, color: T.red, margin: "8px 0 0", fontWeight: 600 }}>
                    Limit reached. New bookings will be made on-demand at standard Pro rates.
                  </p>
                )}
              </div>
            </>
          ) : <p style={{ fontSize: 13, color: T.n400 }}>No upcoming invoice.</p>}
        </div>
      </div>

      {compareOpen && (
        <div style={{ marginBottom: 20, padding: "20px", borderRadius: 16, border: `1px solid ${T.n150}`, background: T.white }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <h3 style={{ fontSize: 14.5, fontWeight: 700, color: T.n900, margin: 0 }}>Compare plans</h3>
            <button onClick={() => setCompareOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", color: T.n400 }}>
              <Icon name="X" size={16} strokeWidth={2} />
            </button>
          </div>
          <PlanComparisonTable currentPlanKey={plan.key} onSelectPlan={handleSelectPlan} />
        </div>
      )}

      <div style={{ marginBottom: 20 }}>
        <h3 style={{ fontSize: 13, fontWeight: 700, color: T.n900, margin: "0 0 12px", letterSpacing: "0.02em" }}>Payment method</h3>
        <PaymentMethodCard account={account} onChangeMethod={handleChangeMethod} />
      </div>

      <div style={{ border: `1px solid ${T.n150}`, borderRadius: 16, background: T.white, overflow: "hidden" }}>
        <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.n100}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h3 style={{ fontSize: 14.5, fontWeight: 700, color: T.n900, margin: 0 }}>Invoice history</h3>
          <span style={{ fontSize: 12, color: T.n400 }}>{invoices.length} invoice{invoices.length !== 1 ? "s" : ""}</span>
        </div>

        {invoicesLoading ? (
          <div style={{ padding: "40px 20px", textAlign: "center" }}>
            <p style={{ fontSize: 13, color: T.n400 }}>Loading invoices...</p>
          </div>
        ) : invoices.length === 0 ? (
          <div style={{ padding: "48px 32px", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
            <div style={{ width: 60, height: 60, borderRadius: 16, background: T.g50, border: `1px solid ${T.g100}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Icon name="FileText" size={26} color={T.g500} strokeWidth={1.5} />
            </div>
            <div>
              <h4 style={{ fontFamily: T.serif, fontSize: 22, fontWeight: 400, color: T.n900, margin: "0 0 8px" }}>
                No invoices yet
              </h4>
              <p style={{ fontSize: 13.5, color: T.n500, margin: "0 0 20px", maxWidth: 360, lineHeight: 1.7 }}>
                Your invoices will appear here once you make your first booking.
                Start by reserving a Pro or making an on-demand booking.
              </p>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={() => onNavigate?.("team")}
                style={{ padding: "11px 22px", borderRadius: 10, border: "none", background: T.g500, color: T.white, fontSize: 13, fontWeight: 600, fontFamily: T.sans, cursor: "pointer" }}
                onMouseEnter={e => e.currentTarget.style.background = T.g600}
                onMouseLeave={e => e.currentTarget.style.background = T.g500}>
                Reserve a Pro
              </button>
              <button
                onClick={() => onNavigate?.("bookings")}
                style={{ padding: "11px 22px", borderRadius: 10, border: `1.5px solid ${T.n200}`, background: "transparent", color: T.n700, fontSize: 13, fontWeight: 600, fontFamily: T.sans, cursor: "pointer" }}>
                Make a booking
              </button>
            </div>
          </div>
        ) : (
          invoices.map(inv => (
            <InvoiceRow key={inv.id} inv={inv} onDownload={() => handleDownloadInvoice(inv)} />
          ))
        )}
      </div>

      <ChangePlanModal
        open={!!targetPlan}
        targetPlanKey={targetPlan}
        currentAccount={account}
        onClose={() => setTargetPlan(null)}
        onConfirm={handleConfirmPlanChange}
      />
    </div>
  );
}